import React, { useState, useEffect } from 'react';
import { costiApprovalAPI, commesseAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Check, 
  X,
  Loader2,
  Plane,
  Receipt,
  Building2,
  CheckCircle2,
  XCircle
} from 'lucide-react';

export const CostiApprovalPage = () => {
  const { user } = useAuth();
  const [trasferte, setTrasferte] = useState([]);
  const [costiEsterni, setCostiEsterni] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTrasferte, setSelectedTrasferte] = useState([]);
  const [selectedCosti, setSelectedCosti] = useState([]);
  const [filterCommessa, setFilterCommessa] = useState('all');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectType, setRejectType] = useState(null); // 'trasferta' or 'costo_esterno'
  const [rejectReason, setRejectReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('trasferte');

  const canAccess = user?.ruolo === 'admin' || user?.ruolo === 'coordinator' || user?.ruolo === 'pm';

  useEffect(() => {
    if (canAccess) {
      loadData();
    }
  }, [canAccess, filterCommessa]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [pendingRes, commesseRes] = await Promise.all([
        costiApprovalAPI.getPending(null, filterCommessa !== 'all' ? filterCommessa : null),
        commesseAPI.getAll()
      ]);
      setTrasferte(pendingRes.data.trasferte || []);
      setCostiEsterni(pendingRes.data.costi_esterni || []);
      setCommesse(commesseRes.data.filter(c => c.stato === 'attiva'));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTrasferta = (id) => {
    setSelectedTrasferte(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleCosto = (id) => {
    setSelectedCosti(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllTrasferte = () => {
    if (selectedTrasferte.length === trasferte.length) {
      setSelectedTrasferte([]);
    } else {
      setSelectedTrasferte(trasferte.map(t => t.id));
    }
  };

  const selectAllCosti = () => {
    if (selectedCosti.length === costiEsterni.length) {
      setSelectedCosti([]);
    } else {
      setSelectedCosti(costiEsterni.map(c => c.id));
    }
  };

  const handleApprove = async (tipo) => {
    const ids = tipo === 'trasferta' ? selectedTrasferte : selectedCosti;
    if (ids.length === 0) {
      toast.error('Seleziona almeno un elemento');
      return;
    }

    setIsSubmitting(true);
    try {
      await costiApprovalAPI.approve({
        entry_ids: ids,
        tipo: tipo,
        azione: 'approva'
      });
      toast.success(`${ids.length} ${tipo === 'trasferta' ? 'trasferte approvate' : 'costi approvati'}`);
      if (tipo === 'trasferta') {
        setSelectedTrasferte([]);
      } else {
        setSelectedCosti([]);
      }
      loadData();
    } catch (error) {
      console.error('Error approving:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message).join(', ') 
        : (typeof detail === 'string' ? detail : 'Errore nell\'approvazione');
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openRejectDialog = (tipo) => {
    const ids = tipo === 'trasferta' ? selectedTrasferte : selectedCosti;
    if (ids.length === 0) {
      toast.error('Seleziona almeno un elemento');
      return;
    }
    setRejectType(tipo);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    const ids = rejectType === 'trasferta' ? selectedTrasferte : selectedCosti;
    
    setIsSubmitting(true);
    try {
      await costiApprovalAPI.approve({
        entry_ids: ids,
        tipo: rejectType,
        azione: 'respingi',
        motivo: rejectReason || 'Respinto'
      });
      toast.success(`${ids.length} ${rejectType === 'trasferta' ? 'trasferte respinte' : 'costi respinti'}`);
      if (rejectType === 'trasferta') {
        setSelectedTrasferte([]);
      } else {
        setSelectedCosti([]);
      }
      setRejectDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error rejecting:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message).join(', ') 
        : (typeof detail === 'string' ? detail : 'Errore nel respingimento');
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Non hai i permessi per visualizzare questa pagina</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="costi-approval-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const totalePendingTrasferte = trasferte.reduce((acc, t) => acc + (t.importo || 0), 0);
  const totalePendingCosti = costiEsterni.reduce((acc, c) => acc + (c.importo || 0), 0);

  return (
    <div className="space-y-6" data-testid="costi-approval-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Approvazione Costi</h1>
        <p className="text-muted-foreground">Approva o respingi trasferte e costi esterni in attesa</p>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label htmlFor="filter-commessa" className="mb-2 block">Filtra per Commessa</Label>
              <Select value={filterCommessa} onValueChange={setFilterCommessa}>
                <SelectTrigger id="filter-commessa" data-testid="filter-commessa-select">
                  <SelectValue placeholder="Tutte le commesse" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le commesse</SelectItem>
                  {commesse.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.codice_commessa} - {c.nome_progetto}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4">
              <div className="bg-muted rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">Trasferte in attesa</p>
                <p className="text-lg font-bold font-mono">{formatCurrency(totalePendingTrasferte)}</p>
                <Badge variant="secondary">{trasferte.length}</Badge>
              </div>
              <div className="bg-muted rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">Costi esterni in attesa</p>
                <p className="text-lg font-bold font-mono">{formatCurrency(totalePendingCosti)}</p>
                <Badge variant="secondary">{costiEsterni.length}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="trasferte" className="flex items-center gap-2">
            <Plane className="h-4 w-4" />
            Trasferte ({trasferte.length})
          </TabsTrigger>
          <TabsTrigger value="costi" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Costi Esterni ({costiEsterni.length})
          </TabsTrigger>
        </TabsList>

        {/* Trasferte Tab */}
        <TabsContent value="trasferte">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Trasferte in Attesa</CardTitle>
                <CardDescription>
                  {selectedTrasferte.length > 0 
                    ? `${selectedTrasferte.length} selezionate` 
                    : 'Seleziona le trasferte da approvare o respingere'}
                </CardDescription>
              </div>
              {selectedTrasferte.length > 0 && (
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleApprove('trasferta')} 
                    disabled={isSubmitting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="approve-trasferte-btn"
                  >
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Approva ({selectedTrasferte.length})
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => openRejectDialog('trasferta')}
                    disabled={isSubmitting}
                    data-testid="reject-trasferte-btn"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Respingi
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {trasferte.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                  <p>Nessuna trasferta in attesa di approvazione</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox 
                          checked={selectedTrasferte.length === trasferte.length && trasferte.length > 0}
                          onCheckedChange={selectAllTrasferte}
                          data-testid="select-all-trasferte"
                        />
                      </TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Commessa</TableHead>
                      <TableHead>Risorsa</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trasferte.map((entry) => (
                      <TableRow key={entry.id} data-testid={`trasferta-row-${entry.id}`}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedTrasferte.includes(entry.id)}
                            onCheckedChange={() => toggleTrasferta(entry.id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{entry.data}</TableCell>
                        <TableCell>
                          <div>
                            <span className="font-mono text-xs text-muted-foreground">{entry.commessa_codice}</span>
                            <p className="text-sm font-medium">{entry.commessa_nome}</p>
                          </div>
                        </TableCell>
                        <TableCell>{entry.risorsa_nome || '-'}</TableCell>
                        <TableCell className="capitalize">{entry.categoria}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{entry.descrizione || '-'}</TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          {formatCurrency(entry.importo)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Costi Esterni Tab */}
        <TabsContent value="costi">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Costi Esterni in Attesa</CardTitle>
                <CardDescription>
                  {selectedCosti.length > 0 
                    ? `${selectedCosti.length} selezionati` 
                    : 'Seleziona i costi da approvare o respingere'}
                </CardDescription>
              </div>
              {selectedCosti.length > 0 && (
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleApprove('costo_esterno')} 
                    disabled={isSubmitting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="approve-costi-btn"
                  >
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Approva ({selectedCosti.length})
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => openRejectDialog('costo_esterno')}
                    disabled={isSubmitting}
                    data-testid="reject-costi-btn"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Respingi
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {costiEsterni.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                  <p>Nessun costo esterno in attesa di approvazione</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox 
                          checked={selectedCosti.length === costiEsterni.length && costiEsterni.length > 0}
                          onCheckedChange={selectAllCosti}
                          data-testid="select-all-costi"
                        />
                      </TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Commessa</TableHead>
                      <TableHead>Inserito da</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Fornitore</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costiEsterni.map((entry) => (
                      <TableRow key={entry.id} data-testid={`costo-row-${entry.id}`}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedCosti.includes(entry.id)}
                            onCheckedChange={() => toggleCosto(entry.id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{entry.data}</TableCell>
                        <TableCell>
                          <div>
                            <span className="font-mono text-xs text-muted-foreground">{entry.commessa_codice}</span>
                            <p className="text-sm font-medium">{entry.commessa_nome}</p>
                          </div>
                        </TableCell>
                        <TableCell>{entry.inserito_da_nome || '-'}</TableCell>
                        <TableCell className="capitalize">{entry.categoria}</TableCell>
                        <TableCell>{entry.fornitore || '-'}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{entry.descrizione || '-'}</TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          {formatCurrency(entry.importo)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Respingi {rejectType === 'trasferta' ? 'Trasferte' : 'Costi Esterni'}</DialogTitle>
            <DialogDescription>
              Inserisci il motivo del respingimento (opzionale)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Motivo</Label>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Inserisci il motivo del respingimento..."
                rows={3}
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={isSubmitting}
              data-testid="confirm-reject-btn"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Conferma Respingimento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CostiApprovalPage;
